import crypto from 'crypto'

import type { ExecutionContext } from '../context/execution-context'
import type { Extension } from '../extension'
import { z } from '../params'
import type { SystemMethodSpec } from '../system-method'
import { OAUTH_SYSTEM_METHODS } from './method-names'
import { OAuthCallbackObjectSchema, RefreshTokenSchema } from './system-params'
import type {
  OAuth2Props,
  OAuthBag,
  OAuthCallbackObject,
  OAuthCallbackResult,
  OAuthRefreshResult,
} from './types'

// `ext.setupOauth2(...)` — the OAuth2 subsystem (06-oauth2.md). Declarative for the common case, with an
// escape hatch per step for providers that deviate. It owns the three v1 SYSTEM methods, the frozen runtime
// seam the platform drives:
//
//   getOAuth2ConnectionURL (GET)  → where to send the user
//   executeCallback        (POST) → trade the code for a token, and name the connection
//   refreshToken           (PUT)  → trade a refresh token for the next access token

export class OAuth2 {
  constructor(private readonly extension: Extension, private readonly props: OAuth2Props) {
    this.#registerSystemMethods()
  }

  // The handler bag's `oauth`: the header access token, plus its deserialized composite payload when the
  // author declared a deserializer (the pattern for providers whose "token" carries a realm or instance id).
  buildBag(accessToken?: string): OAuthBag {
    const token = this.props.deserializeToken && accessToken != null
      ? this.props.deserializeToken(accessToken)
      : undefined

    return { accessToken, token }
  }

  // ── SYSTEM seam: name · input · return · handler, in one table ─────────────

  #registerSystemMethods(): void {
    const { CONNECTION_URL, EXECUTE_CALLBACK, REFRESH_TOKEN } = OAUTH_SYSTEM_METHODS

    // Every step runs where no usable access token exists yet — connect precedes one entirely, and the
    // callback and refresh are what PRODUCE the next one — so each sees a TOKENLESS context. The single
    // exception is the callback's identity lookup, which must use the token it just obtained (#handleCallback).
    const anonymous = (ctx: ExecutionContext): ExecutionContext => ctx.withAccessToken(undefined)

    this.extension.registerSystemMethods([
      {
        name: CONNECTION_URL,
        // Takes no input at all — no scheme.
        returnType: 'STRING',
        run: (_params, ctx) => this.#connectionUrl(anonymous(ctx), this.#composeScopes()),
      },
      {
        name: EXECUTE_CALLBACK,
        scheme: z.object({ callbackObject: OAuthCallbackObjectSchema }),
        run: ({ callbackObject }, ctx) => this.#handleCallback(callbackObject as OAuthCallbackObject, anonymous(ctx)),
      },
      {
        name: REFRESH_TOKEN,
        scheme: z.object({ refreshToken: RefreshTokenSchema }),
        run: ({ refreshToken }, ctx) => this.#refresh(refreshToken as string, anonymous(ctx)),
      },
    ] satisfies SystemMethodSpec[])
  }

  /**
   * The scopes to ask the user for: the base set ∪ every registered item's `oauth2Scopes`.
   *
   * Composed ON DEMAND, when the connect step actually runs, and never stored. `setupOauth2(...)` may be
   * called BEFORE the actions and triggers that carry scopes are registered — a service file is free to
   * order those however it likes — so a set computed at setup time would silently omit whatever registered
   * afterwards, and the user would be sent to a consent screen missing permissions the service needs.
   */
  #composeScopes(): string[] {
    return [...new Set([...(this.props.scopes ?? []), ...this.extension.declaredOAuthScopes()])]
  }

  // ── the three steps ────────────────────────────────────────────────────────

  async #connectionUrl(ctx: ExecutionContext, scopes: string[]): Promise<string> {
    if (this.props.getConnectionURL) {
      return this.props.getConnectionURL(ctx.bagWith({ scopes }))
    }

    const { id } = this.#clientCreds(ctx.config)
    const params = new URLSearchParams({ client_id: id, response_type: 'code', scope: scopes.join(' ') })

    if (this.props.pkce) {
      // The verifier round-trips through `state`, which the console hands back on the callback.
      const verifier = crypto.randomBytes(32).toString('base64url')

      params.set('code_challenge', crypto.createHash('sha256').update(verifier).digest('base64url'))
      params.set('code_challenge_method', 'S256')
      params.set('state', verifier)
    }

    return `${ this.props.authorizeUrl }?${ params.toString() }`
  }

  async #handleCallback(callbackObject: OAuthCallbackObject, ctx: ExecutionContext): Promise<OAuthCallbackResult> {
    if (this.props.executeCallback) {
      return this.props.executeCallback(ctx.bagWith({ callbackObject }))
    }

    const tokenResponse = await this.#exchangeToken(ctx.config, {
      grant_type: 'authorization_code',
      code: callbackObject.code ?? '',
      redirect_uri: callbackObject.redirectURI ?? '',
      ...(callbackObject.state ? { code_verifier: callbackObject.state } : {}),
    })

    const accessToken = tokenResponse.access_token as string
    const refreshToken = tokenResponse.refresh_token as string | undefined

    // Identity lookup runs against the freshly-obtained token — the one exception to the tokenless rule.
    let identity: { name?: string; imageUrl?: string | null; userData?: unknown } = {}

    if (this.props.identityRequest) {
      const raw = await this.props.identityRequest(ctx.withAccessToken(accessToken).toBag())

      identity = this.props.identity ? this.props.identity(raw) : {}
    }

    return {
      token: this.#serialize(accessToken, refreshToken, identity.userData),
      expirationInSeconds: tokenResponse.expires_in as number | undefined,
      refreshToken,
      connectionIdentityName: identity.name || 'Unknown Account',
      connectionIdentityImageURL: identity.imageUrl ?? null,
      overwrite: true,
      userData: identity.userData,
    }
  }

  async #refresh(currentRefreshToken: string, ctx: ExecutionContext): Promise<OAuthRefreshResult> {
    if (this.props.refreshToken) {
      return this.props.refreshToken(ctx.bagWith({ refreshToken: currentRefreshToken }))
    }

    const tokenResponse = await this.#exchangeToken(ctx.config, {
      grant_type: 'refresh_token',
      refresh_token: currentRefreshToken,
    })

    // Providers that rotate refresh tokens send a new one; those that don't leave the current one valid.
    const nextRefresh = (tokenResponse.refresh_token as string | undefined) ?? currentRefreshToken

    return {
      token: this.#serialize(tokenResponse.access_token as string, nextRefresh),
      expirationInSeconds: tokenResponse.expires_in as number | undefined,
      refreshToken: nextRefresh,
    }
  }

  // ── token exchange ─────────────────────────────────────────────────────────

  #serialize(accessToken: string, refreshToken?: string, userData?: unknown): string {
    return this.props.serializeToken
      ? this.props.serializeToken({ accessToken, refreshToken, userData })
      : accessToken
  }

  #clientCreds(config: Record<string, unknown>): { id: string; secret: string } {
    return {
      id: String(config[this.props.clientIdConfig ?? 'clientId'] ?? ''),
      secret: String(config[this.props.clientSecretConfig ?? 'clientSecret'] ?? ''),
    }
  }

  // x-www-form-urlencoded token exchange against `tokenUrl`; client creds go in the Basic header
  // (`clientAuth: 'basic'`) or the body (default).
  async #exchangeToken(config: Record<string, unknown>, body: Record<string, string>): Promise<Record<string, unknown>> {
    const { id, secret } = this.#clientCreds(config)
    const params = new URLSearchParams(body)
    const headers: Record<string, string> = { 'Content-Type': 'application/x-www-form-urlencoded' }

    if (this.props.clientAuth === 'basic') {
      headers.Authorization = `Basic ${ Buffer.from(`${ id }:${ secret }`).toString('base64') }`
    } else {
      params.set('client_id', id)
      params.set('client_secret', secret)
    }

    return Flowrunner.Request
      .post(this.props.tokenUrl as string)
      .set(headers)
      .send(params.toString()) as Promise<Record<string, unknown>>
  }
}
